import { Autocomplete, TextField } from "@mui/material";
import classNames from "classnames";
import isEqual from "lodash/isEqual";
import * as React from "react";

import { axisPresetText, type AxisPresetName } from "./axisPresetNames";
import styles from "./EntityConfig.module.css";

/** Anything a preset picker can offer: it only needs to know how to name it. */
export interface AxisPresetOption {
  name: AxisPresetName;
}

interface AxisPresetPickerProps<Option extends AxisPresetOption> {
  /** The presets to offer, in the order the list shows them. */
  options: readonly Option[];
  /** The preset the entity matches now; undefined leaves the field empty. */
  value: Option | undefined;
  /**
   * Called with the preset picked from the list. Free text picks nothing, so
   * this never reports a value outside `options`.
   */
  onChange: (option: Option) => void;
  /** Spacing from what sits above the picker, where the section needs it. */
  className?: string;
}

/**
 * The "Preset" field every 3D entity section picks its axis convention with:
 * Position, Rotation, Vector Field and Point Cloud.
 *
 * The position picker's layout, shared so the sections read alike: the field
 * shows the picked preset's name and units, and each option shows its name in
 * bold, where it is common in italics, and its units on a line of their own
 * when it has them.
 *
 * Free text is accepted and ignored, as the position picker always did: typing
 * filters the list, and a picker with no matching preset shows an empty field
 * rather than a stale one.
 */
export function AxisPresetPicker<Option extends AxisPresetOption>({
  options,
  value,
  onChange,
  className,
}: AxisPresetPickerProps<Option>) {
  return (
    <Autocomplete
      freeSolo
      size="small"
      fullWidth
      options={options}
      getOptionLabel={(option) =>
        typeof option === "string" ? option : axisPresetText(option.name)
      }
      renderOption={(optionProps, option) => {
        // MUI passes the row's key among the props without typing it; spread
        // into JSX, React warns about it.
        const { key, ...rowProps } = optionProps as typeof optionProps & {
          key: React.Key;
        };
        return (
          <li key={key} {...rowProps}>
            <p className={styles.input}>
              <span className={styles.highlight}>{option.name.title}</span>
              {option.name.formatHint && (
                <i> {`(${option.name.formatHint})`}</i>
              )}
              {option.name.subtitle && (
                <>
                  <br />
                  <i>{option.name.subtitle}</i>
                </>
              )}
            </p>
          </li>
        );
      }}
      isOptionEqualToValue={(option, picked) => isEqual(option, picked)}
      onChange={(_, picked) => {
        if (!!picked && typeof picked === "object") {
          onChange(picked);
        }
      }}
      value={value ?? ""}
      className={classNames([styles.input, styles.field, className])}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Preset"
          InputProps={{ ...params.InputProps, className: styles.input }}
          InputLabelProps={{
            ...params.InputLabelProps,
            shrink: true,
            className: styles.input,
          }}
        />
      )}
    />
  );
}
